import * as THREE from 'three';
import type { GameContext } from '../core/context';
import { Rng } from '../core/rng';
import type { System } from '../core/system';
import { onMood } from '../render/mood';
import type { Quality } from '../render/post';
import { qualityTier } from '../render/qualityTier';
import { viewpointFocus } from '../render/viewpoints';

/**
 * Ambient atmosphere (owner: world-artist): a light drizzle that follows the camera and smoke from
 * the district's industrial chimneys. Purely visual — no simulation state — one draw call each.
 */
export type Weather = 'clear' | 'drizzle' | 'rain';

/** Current weather, mirrored read-only for other fx (footsteps). Written only by this module. */
export const weatherState: { current: Weather } = { current: 'drizzle' };

declare module '../core/hooks' {
  interface BhHooks {
    /** 'clear' | 'drizzle' | 'rain' */
    setWeather(kind: string): void;
  }
}

const RAIN_COUNT = 1400;
/**
 * Share of the streak pool a tier animates and draws. On a phone (MEDIUM) the full count reads as static
 * over the picture; fewer streaks keep the weather and cost less to update and draw.
 */
const RAIN_TIER: Record<Quality, number> = { high: 1, medium: 0.6, low: 0.4 };
const BOX_X = 80;
const BOX_Z = 60;
const BOX_Y = 45;

const SMOKE_VERT = /* glsl */ `
  attribute float aSize;
  attribute float aAlpha;
  varying float vAlpha;
  #include <fog_pars_vertex>
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
    vAlpha = aAlpha;
    #include <fog_vertex>
  }`;
const SMOKE_FRAG = /* glsl */ `
  uniform vec3 uColour;
  varying float vAlpha;
  #include <fog_pars_fragment>
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r = length(d);
    float a = smoothstep(0.5, 0.0, r) * vAlpha;
    if (a < 0.01) discard;
    gl_FragColor = vec4(uColour, a);
    #include <fog_fragment>
  }`;

interface Puff {
  x: number;
  y: number;
  z: number;
  age: number;
  life: number;
  emitter: number;
  /** 1 = factory stack plume, < 1 = thin domestic coal-fire wisp. */
  scale: number;
}

/** Deterministic hash -> [0,1) (same mixing as the city view's, kept local to avoid a dependency). */
function hash01(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}

export function createAmbientFxSystem(): System {
  let ctx: GameContext;
  let weather: Weather = 'drizzle';
  /** Eased rain density (0 clear, 0.55 drizzle, 1 rain): follows `weather` over several seconds. */
  let rainLevel = -1;
  let unsubMood: (() => void) | null = null;
  const rng = new Rng(4711);
  let rain: THREE.LineSegments | null = null;
  let rainPos: Float32Array | null = null;
  const drops: { x: number; y: number; z: number; v: number }[] = [];
  let smoke: THREE.Points | null = null;
  let smokeGeo: THREE.BufferGeometry | null = null;
  const puffs: Puff[] = [];
  const emitters: { x: number; y: number; z: number; rate: number; acc: number; scale: number }[] = [];
  const wind = { x: 1.4, z: -0.3 };
  let rainMat: THREE.LineBasicMaterial | null = null;
  /** Smoke tint per mood: unlit grey smoke would glow like fog lamps against a night street. */
  const smokeColour = new THREE.Color(0.32, 0.31, 0.33);

  const focus = (): { x: number; z: number } => viewpointFocus() ?? ctx.services.tryGet('cameraRig')?.lookAt ?? { x: 0, z: 0 };

  return {
    id: 'ambient-fx',
    order: 720,
    init(c) {
      ctx = c;
      unsubMood = onMood((m) => {
        weather = m.weather;
        weatherState.current = weather;
        if (m.id === 'night') smokeColour.setRGB(0.05, 0.055, 0.07);
        else if (m.id === 'overcast') smokeColour.setRGB(0.34, 0.33, 0.32);
        else smokeColour.setRGB(0.32, 0.31, 0.33);
        const u = (smoke?.material as THREE.ShaderMaterial | undefined)?.uniforms['uColour'];
        if (u) (u.value as THREE.Color).copy(smokeColour);
      });
      c.hooks.register('setWeather', (kind) => {
        if (kind === 'clear' || kind === 'drizzle' || kind === 'rain') {
          weather = kind;
          weatherState.current = weather;
          rainLevel = -1; // a debug/test switch shows at once
        }
      });
      if (!c.render) return;

      // ---- drizzle streaks
      rainPos = new Float32Array(RAIN_COUNT * 6);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(rainPos, 3).setUsage(THREE.DynamicDrawUsage));
      rainMat = new THREE.LineBasicMaterial({ color: new THREE.Color(0.62, 0.68, 0.8), transparent: true, opacity: 0.22, depthWrite: false });
      rain = new THREE.LineSegments(g, rainMat);
      rain.frustumCulled = false;
      rain.name = 'fx-rain';
      rain.renderOrder = 5;
      for (let i = 0; i < RAIN_COUNT; i++) drops.push({ x: rng.range(-BOX_X, BOX_X), y: rng.range(0, BOX_Y), z: rng.range(-BOX_Z, BOX_Z), v: rng.range(8, 11) });
      c.scene.add(rain);
    },
    start() {
      if (!ctx.render) return;
      // ---- chimney smoke: factory chimneys and the gasworks
      const layout = ctx.services.tryGet('layout');
      if (layout) {
        for (const lot of layout.buildings) {
          if (lot.style === 'factory' && lot.tags.includes('chimney')) {
            const lx = -lot.halfX + 3;
            const lz = -lot.halfZ + 3;
            const cs = Math.cos(lot.angle);
            const sn = Math.sin(lot.angle);
            emitters.push({ x: lot.center.x + lx * cs + lz * sn, y: 26.5, z: lot.center.z - lx * sn + lz * cs, rate: 2.2, acc: 0, scale: 1 });
          }
          // Coal fires in the terraces (1970: most houses still burn coal): a thin wisp from some
          // party-wall stacks. Mirrors the stack placement in world/view/buildings.ts (terrace()).
          if (lot.style === 'terrace') {
            const W = lot.halfX * 2;
            const unitsTag = lot.tags.find((t) => t.startsWith('units:'));
            const units = Math.max(1, Number(unitsTag ? unitsTag.slice(6) : Math.round(W / 5.2)));
            const rise = lot.halfZ * Math.tan(0.62);
            const cs = Math.cos(lot.angle);
            const sn = Math.sin(lot.angle);
            for (let u = 0; u <= units; u++) {
              if (hash01(lot.id, u, 811) > 0.22) continue;
              const lx = Math.max(-W / 2 + 0.4, Math.min(W / 2 - 0.4, -W / 2 + (u * W) / units));
              emitters.push({ x: lot.center.x + lx * cs, y: lot.height + rise + 1.9, z: lot.center.z - lx * sn, rate: 0.9, acc: hash01(lot.id, u, 812), scale: 0.38 });
            }
          }
        }
      }
      const max = Math.max(1, emitters.length) * 40;
      smokeGeo = new THREE.BufferGeometry();
      smokeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(max * 3), 3).setUsage(THREE.DynamicDrawUsage));
      smokeGeo.setAttribute('aSize', new THREE.BufferAttribute(new Float32Array(max), 1).setUsage(THREE.DynamicDrawUsage));
      smokeGeo.setAttribute('aAlpha', new THREE.BufferAttribute(new Float32Array(max), 1).setUsage(THREE.DynamicDrawUsage));
      smokeGeo.setDrawRange(0, 0);
      const mat = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, { uColour: { value: smokeColour.clone() } }]),
        vertexShader: SMOKE_VERT,
        fragmentShader: SMOKE_FRAG,
        transparent: true,
        depthWrite: false,
        fog: true,
      });
      smoke = new THREE.Points(smokeGeo, mat);
      smoke.frustumCulled = false;
      smoke.name = 'fx-smoke';
      smoke.renderOrder = 4;
      ctx.scene.add(smoke);
    },
    update(dt) {
      if (!ctx.render || dt <= 0) return;
      const d = Math.min(dt, 0.1);
      const f = focus();
      // ---- rain
      if (rain && rainPos && rainMat) {
        // The drizzle sets in and lifts over ~8 s (the mood switches weather once, mid-fade): ease the
        // streak count and opacity instead of popping them on or off.
        const want = weather === 'clear' ? 0 : weather === 'drizzle' ? 0.55 : 1;
        if (rainLevel < 0) rainLevel = want;
        else rainLevel += (want - rainLevel) * Math.min(1, d * 0.5);
        if (Math.abs(want - rainLevel) < 0.005) rainLevel = want;
        const active = rainLevel;
        rain.visible = active > 0.01;
        rainMat.opacity = 0.2 + 0.12 * Math.max(0, (active - 0.55) / 0.45);
        const n = Math.floor(RAIN_COUNT * active * RAIN_TIER[qualityTier()]);
        const len = weather === 'rain' ? 0.075 : 0.05;
        for (let i = 0; i < n; i++) {
          const p = drops[i]!;
          p.y -= p.v * d;
          p.x += wind.x * d;
          p.z += wind.z * d;
          // keep drops in a box around the focus (wrap)
          let rx = p.x - f.x;
          let rz = p.z - (f.z + 10);
          if (p.y < 0 || rx > BOX_X || rx < -BOX_X || rz > BOX_Z || rz < -BOX_Z) {
            if (p.y < 0) p.y += BOX_Y;
            if (rx > BOX_X) rx -= BOX_X * 2;
            else if (rx < -BOX_X) rx += BOX_X * 2;
            if (rz > BOX_Z) rz -= BOX_Z * 2;
            else if (rz < -BOX_Z) rz += BOX_Z * 2;
            p.x = f.x + rx;
            p.z = f.z + 10 + rz;
          }
          // drops high in the box pass close to the camera and draw long: shorten them with height
          const l = len * (1 - 0.55 * (p.y / BOX_Y));
          const o = i * 6;
          rainPos[o] = p.x;
          rainPos[o + 1] = p.y;
          rainPos[o + 2] = p.z;
          rainPos[o + 3] = p.x - wind.x * l;
          rainPos[o + 4] = p.y + p.v * l;
          rainPos[o + 5] = p.z - wind.z * l;
        }
        rain.geometry.setDrawRange(0, n * 2);
        (rain.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      }
      // ---- smoke
      if (smoke && smokeGeo) {
        emitters.forEach((e, ei) => {
          // domestic wisps only near the camera (they're too thin to read further out)
          if (e.scale < 1 && Math.abs(e.x - f.x) + Math.abs(e.z - f.z) > 170) return;
          e.acc += e.rate * d;
          while (e.acc >= 1) {
            e.acc -= 1;
            const jit = 0.4 * e.scale;
            if (puffs.length < emitters.length * 40) puffs.push({ x: e.x + rng.range(-jit, jit), y: e.y, z: e.z + rng.range(-jit, jit), age: 0, life: rng.range(9, 13) * (e.scale < 1 ? 0.7 : 1), emitter: ei, scale: e.scale });
          }
        });
        const pos = smokeGeo.getAttribute('position') as THREE.BufferAttribute;
        const size = smokeGeo.getAttribute('aSize') as THREE.BufferAttribute;
        const alpha = smokeGeo.getAttribute('aAlpha') as THREE.BufferAttribute;
        let k = 0;
        for (let i = puffs.length - 1; i >= 0; i--) {
          const p = puffs[i]!;
          p.age += d;
          if (p.age >= p.life) {
            puffs.splice(i, 1);
            continue;
          }
          const t = p.age / p.life;
          p.y += (1.6 - t) * d * (p.scale < 1 ? 0.6 : 1);
          p.x += wind.x * 1.2 * d * (0.4 + t);
          p.z += wind.z * 1.2 * d * (0.4 + t);
          pos.setXYZ(k, p.x, p.y, p.z);
          size.setX(k, (3 + t * 16) * p.scale);
          alpha.setX(k, Math.min(1, t * 6) * (1 - t) * (p.scale < 1 ? 0.4 : 0.55));
          k++;
        }
        smokeGeo.setDrawRange(0, k);
        pos.needsUpdate = true;
        size.needsUpdate = true;
        alpha.needsUpdate = true;
      }
    },
    dispose() {
      unsubMood?.();
      if (rain) {
        ctx.scene.remove(rain);
        rain.geometry.dispose();
        rainMat?.dispose();
      }
      if (smoke) {
        ctx.scene.remove(smoke);
        smokeGeo?.dispose();
        (smoke.material as THREE.Material).dispose();
      }
    },
  };
}
